import { useEffect, useRef } from 'react';

interface ResultCardProps {
  result: {
    ts: number;
    M: number;
    a: number;
    hc: number;
    pf: number;
    isExtrapolated: boolean;
    note: string;
  } | null;
}

function pfToColor(pf: number): string {
  if (pf >= 80) return 'text-primary-600';
  if (pf >= 60) return 'text-accent-600';
  if (pf >= 40) return 'text-yellow-600';
  return 'text-red-500';
}

function pfToBg(pf: number): string {
  if (pf >= 80) return 'bg-primary-50 border-primary-200';
  if (pf >= 60) return 'bg-accent-50 border-accent-200';
  if (pf >= 40) return 'bg-yellow-50 border-yellow-200';
  return 'bg-red-50 border-red-200';
}

export default function ResultCard({ result }: ResultCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (result && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [result]);

  if (!result) return null;

  return (
    <div
      ref={cardRef}
      className={`rounded-2xl p-5 md:p-8 border ${pfToBg(result.pf)} animate-fade-slide-up`}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-background-100 flex items-center justify-center">
          <i className="ri-bar-chart-box-line text-foreground-600 text-xl" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foreground-950">计算结果</h2>
          <p className="text-sm text-foreground-600 mt-0.5">基于 Puro Earth 方法学 Equation 6.4</p>
        </div>
      </div>

      <div className="text-center mb-8 py-6 rounded-xl bg-background-50/80 border border-background-200/50">
        <div className={`text-6xl md:text-7xl font-bold tracking-tight ${pfToColor(result.pf)}`}>
          {result.pf.toFixed(2)}
          <span className="text-2xl md:text-3xl font-medium text-foreground-500 ml-1">%</span>
        </div>
        <p className="text-sm text-foreground-600 mt-2">生物炭碳持久系数 PF</p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-background-200/70 mb-5">
        <table className="w-full text-sm min-w-[420px]">
          <thead>
            <tr className="bg-background-100/80">
              <th className="text-left px-4 py-3 font-medium text-foreground-500">变量</th>
              <th className="text-left px-4 py-3 font-medium text-foreground-500">数值</th>
              <th className="text-left px-4 py-3 font-medium text-foreground-500">说明</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200/50">
            <tr>
              <td className="px-4 py-3 text-foreground-700 font-medium">Ts</td>
              <td className="px-4 py-3 text-foreground-950">{result.ts.toFixed(2)}</td>
              <td className="px-4 py-3 text-foreground-500 text-xs">土壤温度（°C）</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-foreground-700 font-medium">M</td>
              <td className="px-4 py-3 text-foreground-950">{result.M.toFixed(2)}</td>
              <td className="px-4 py-3 text-foreground-500 text-xs">回归参数（Table 6.1 线性插值）</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-foreground-700 font-medium">a</td>
              <td className="px-4 py-3 text-foreground-950">{result.a.toFixed(2)}</td>
              <td className="px-4 py-3 text-foreground-500 text-xs">回归参数（Table 6.1 线性插值）</td>
            </tr>
            <tr>
              <td className="px-4 py-3 text-foreground-700 font-medium">H/C_org</td>
              <td className="px-4 py-3 text-foreground-950">{result.hc.toFixed(4)}</td>
              <td className="px-4 py-3 text-foreground-500 text-xs">输入值（实测摩尔比）</td>
            </tr>
            <tr className="bg-background-50/50">
              <td className="px-4 py-3 text-primary-700 font-semibold">PF</td>
              <td className="px-4 py-3 text-primary-700 font-semibold">{result.pf.toFixed(2)} %</td>
              <td className="px-4 py-3 text-foreground-500 text-xs">= M – a × H/C_org</td>
            </tr>
          </tbody>
        </table>
      </div>

      {result.isExtrapolated && (
        <div className="p-4 rounded-xl bg-yellow-50 border border-yellow-200 flex items-start gap-3 mb-4">
          <i className="ri-alert-line text-yellow-600 mt-0.5" />
          <p className="text-sm text-yellow-800">{result.note}</p>
        </div>
      )}

      <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200/60">
        <p className="text-xs text-foreground-600 leading-relaxed">
          持久碳损失量：C_loss = C_stored × (100 − PF) / 100（Equation 6.3），需另行输入该批次 C_stored 计算
        </p>
      </div>
    </div>
  );
}